import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { useACHIsEditing } from "Payments/AddPaymentMethod/paymentMethodStateHooks";

import { FnrPopoverWithScroll } from "UI/FnrPopover2";

import capitalize from "Utilities/capitalize";

const fundColor = "#ffffff";

export default function Dropdown({
  name,
  options,
  disabled,
  handleSelection,
  selected,
  tabindex,
}) {
  const ref = useRef();
  const [isEditing] = useACHIsEditing();

  const [show, setShow] = useState(false);

  const toggle = () => {
    if (disabled) return;
    setShow((currentShow) => !currentShow);
  };

  return (
    <React.Fragment>
      <Button
        data-cy="recurringIntervalListBtn"
        ref={ref}
        onClick={toggle}
        color={fundColor}
        disabled={disabled || !isEditing}
        $isEditing={isEditing}
        open={show}
        tabIndex={tabindex}
      >
        <BtnText $color={fundColor}>{capitalize(selected) || name}</BtnText>
        <DropdownIcon icon={faChevronDown} $show={show} />
      </Button>

      <FnrPopoverWithScroll
        buttonRef={ref}
        show={show}
        color="white"
        location="bottom"
        onShouldClose={() => setShow(false)}
      >
        <OptionsWrapper>
          <OptionsLabel>{name}</OptionsLabel>

          {options.map((option) => {
            return (
              <Option
                data-cy={option.id}
                key={option.id}
                onClick={() => handleSelection(option)}
                // $selected={option.id === selected}
                $fundColor={fundColor}
              >
                <OptionText>{option?.label}</OptionText>
                {option.id === selected && (
                  <i className="fas fa-check" style={{ marginLeft: 8 }} />
                )}
              </Option>
            );
          })}
        </OptionsWrapper>
      </FnrPopoverWithScroll>
    </React.Fragment>
  );
}

Dropdown.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })
  ),
  disabled: PropTypes.bool,
  selected: PropTypes.string,
  handleSelection: PropTypes.func,
  tabindex: PropTypes.number,
};

const Button = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${(props) => props.color};
  background-color: ${({ open }) =>
    open ? "rgba(255, 255, 255, 0.1)" : "transparent"};
  padding: 3px 6px;
  font-weight: 600;
  border: 2px solid
    ${({ open }) => (open ? "rgba(255, 255, 255, 0.6)" : "transparent")};
  box-shadow: ${({ $isEditing }) =>
    `0 0 0 1px rgba(255, 255, 255, ${$isEditing ? 0.175 : 0}) inset`};
  border-radius: 8px;
  min-width: 87px;
  transition: all 0.6s ease-in-out;
  pointer-events: ${({ disabled, $isEditing }) =>
    disabled || !$isEditing ? "none" : "inherit"};
  font-family: Montserrat, "Open Sans", sans-serif;
  cursor: ${({ disabled, $isEditing }) =>
    disabled ? "not-allowed" : !$isEditing ? "inherit" : "pointer"};

  &:hover {
    transition: border 0.2s;
    border-radius: 8px;
    border-width: 2px;
    border-style: ${({ $isEditing }) => ($isEditing ? "solid" : "transparent")};
    border-color: ${({ open, color, $isEditing }) =>
      !$isEditing ? "tranparent" : open ? color : "rgba(255, 255, 255, 0.6)"};
    background: ${({ $isEditing }) =>
      !$isEditing ? "transparent" : "rgba(255, 255, 255, 0.1)"};
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
  }
  &:focus-within {
    transition: border 0.2s;
    border-radius: 8px;
    border: 2px solid
      ${({ open, color }) => (open ? color : "rgba(255, 255, 255, 0.6)")};
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0) inset;
  }
`;

const DropdownIcon = styled(FontAwesomeIcon)`
  transform: ${(props) => `rotate(${props.$show ? "-180deg" : 0})`};
  margin-left: 6px;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: all ease-in-out 0.2s;

  ${Button}:hover &, ${Button}:focus-within & {
    opacity: 1;
  }
`;

const BtnText = styled.span`
  font-weight: 600;
  /* border-bottom: ${(props) => `1px solid ${props.$color}`}; */
  //text-decoration: underline;
  font-size: 12px;
`;

const OptionsWrapper = styled.ul`
  border-radius: 0.5rem;
  background: white;
  color: hsl(0, 0%, 30%);
  list-style: none;
  padding: 1px 4px;
  margin: 0;
`;

const OptionText = styled.span`
  display: inline-block;
  transition: all 0.3s ease-in-out;
`;

const Option = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px;
  padding: 6px 8px;
  border-radius: 0.5rem;
  background: ${(props) => (props.$selected ? props.$fundColor : "none")};
  font-weight: ${(props) => (props.$selected ? 600 : "inherit")};
  font-size: 12px;
  color: ${(props) => (props.$selected ? "white" : "inherit")};

  &:hover {
    background: ${(props) =>
      props.$selected ? props.$fundColor : "hsl(0 0% 95%)"};
    cursor: pointer;

    ${OptionText} {
      transform: scale(1.1);
    }
  }
`;

const OptionsLabel = styled(OptionText)`
  font-size: 10px;
  text-transform: uppercase;
  opacity: 0.75;
  letter-spacing: 1px;
  padding-inline: 6px;
`;
